import {
  TEMPLATE_ID_DUAL_SHARE_CLASS,
  TEMPLATE_ID_LONG_TERM_VALUE_FUND,
  TEMPLATE_ID_OMIT_ETHIK_KONTROVERSE,
} from './templateConstants';
import type { PieChartHeadingKey } from './pieChartHeadings';

export type PieDataField = 'branchen' | 'regionen' | 'marktkapitalisierung' | 'pie_addition';

export type PiePreviewImageKey =
  | 'pieChart1ImageDataUrl'
  | 'pieChart2ImageDataUrl'
  | 'pieChart3ImageDataUrl'
  | 'chart3ImageDataUrl';

export interface PieChartDisplaySlot {
  key: PieChartHeadingKey;
  field: PieDataField;
  donutId: string;
  previewImageKey: PiePreviewImageKey;
}

const SLOT_BRANCHEN: PieChartDisplaySlot = {
  key: 'branchen',
  field: 'branchen',
  donutId: 'donut-branchen',
  previewImageKey: 'pieChart1ImageDataUrl',
};

const SLOT_REGIONEN: PieChartDisplaySlot = {
  key: 'regionen',
  field: 'regionen',
  donutId: 'donut-regionen',
  previewImageKey: 'pieChart2ImageDataUrl',
};

const SLOT_MARKTKAPITALISIERUNG: PieChartDisplaySlot = {
  key: 'marktkapitalisierung',
  field: 'marktkapitalisierung',
  donutId: 'donut-marktkapitalisierung',
  previewImageKey: 'pieChart3ImageDataUrl',
};

const SLOT_PIE_ADDITION: PieChartDisplaySlot = {
  key: 'pie_addition',
  field: 'pie_addition',
  donutId: 'donut-pie-addition',
  previewImageKey: 'chart3ImageDataUrl',
};

/** Default DOM order (field keys match legacy data shape) */
const DEFAULT_DONUT_ORDER: PieChartDisplaySlot[] = [
  SLOT_BRANCHEN,
  SLOT_REGIONEN,
  SLOT_MARKTKAPITALISIERUNG,
];

/** Long-Term Value Fund: keep labels and datasets aligned. */
const LONG_TERM_VALUE_FUND_DISPLAY_ORDER: PieChartDisplaySlot[] = [
  SLOT_MARKTKAPITALISIERUNG,
  SLOT_BRANCHEN,
  SLOT_REGIONEN,
  SLOT_PIE_ADDITION,
];

/**
 * Renamed pie headings (VERMÖGENSVERTEILUNG / BRANCHEN / REGIONEN) — field keys stay in DB
 * but display titles no longer match branchen → regionen → marktkapitalisierung order.
 * BRANCHEN uses marktkapitalisierung data; REGIONEN uses regionen data.
 */
const THREE_PIE_RENAMED_DISPLAY_ORDER: PieChartDisplaySlot[] = [
  {
    key: 'branchen',
    field: 'branchen',
    donutId: 'donut-branchen',
    previewImageKey: 'pieChart1ImageDataUrl',
  },
  {
    key: 'regionen',
    field: 'marktkapitalisierung',
    donutId: 'donut-marktkapitalisierung',
    previewImageKey: 'pieChart3ImageDataUrl',
  },
  {
    key: 'marktkapitalisierung',
    field: 'regionen',
    donutId: 'donut-regionen',
    previewImageKey: 'pieChart2ImageDataUrl',
  },
  SLOT_PIE_ADDITION,
];

const DUAL_SHARE_CLASS_DISPLAY_ORDER: PieChartDisplaySlot[] = [
  ...THREE_PIE_RENAMED_DISPLAY_ORDER.slice(0, 3),
  SLOT_PIE_ADDITION,
];

const TEMPLATE_DONUT_DISPLAY_ORDER: Record<string, PieChartDisplaySlot[]> = {
  [TEMPLATE_ID_LONG_TERM_VALUE_FUND]: LONG_TERM_VALUE_FUND_DISPLAY_ORDER,
  [TEMPLATE_ID_DUAL_SHARE_CLASS]: DUAL_SHARE_CLASS_DISPLAY_ORDER,
  [TEMPLATE_ID_OMIT_ETHIK_KONTROVERSE]: THREE_PIE_RENAMED_DISPLAY_ORDER,
};

/** pie_titles array index per field (display order, not storage field order) */
const PIE_TITLE_INDEX_BY_TEMPLATE: Record<string, Partial<Record<PieChartHeadingKey, number>>> = {
  [TEMPLATE_ID_LONG_TERM_VALUE_FUND]: {
    gesamtportfolio: 0,
    marktkapitalisierung: 1,
    branchen: 2,
    regionen: 3,
  },
};

const DEFAULT_PIE_TITLE_INDEX: Partial<Record<PieChartHeadingKey, number>> = {
  gesamtportfolio: 0,
  branchen: 1,
  regionen: 2,
  marktkapitalisierung: 3,
  pie_addition: 4,
};

export function getPieChartDisplayOrder(templateId?: string): PieChartDisplaySlot[] {
  if (!templateId) return DEFAULT_DONUT_ORDER;
  return TEMPLATE_DONUT_DISPLAY_ORDER[templateId] ?? DEFAULT_DONUT_ORDER;
}

/** Config editors: storage fields with template-specific display labels (via slot.key). */
export function getPieChartConfigOrder(templateId?: string): PieChartDisplaySlot[] {
  if (templateId === TEMPLATE_ID_LONG_TERM_VALUE_FUND) {
    return [SLOT_MARKTKAPITALISIERUNG, SLOT_BRANCHEN, SLOT_REGIONEN, SLOT_PIE_ADDITION];
  }
  if (templateId === TEMPLATE_ID_DUAL_SHARE_CLASS) {
    return DUAL_SHARE_CLASS_DISPLAY_ORDER;
  }
  if (templateId === TEMPLATE_ID_OMIT_ETHIK_KONTROVERSE) {
    return THREE_PIE_RENAMED_DISPLAY_ORDER;
  }
  return getPieChartDisplayOrder(templateId);
}

export function getPieTitleIndex(
  templateId: string | undefined,
  key: PieChartHeadingKey,
): number | undefined {
  if (templateId && PIE_TITLE_INDEX_BY_TEMPLATE[templateId]?.[key] !== undefined) {
    return PIE_TITLE_INDEX_BY_TEMPLATE[templateId][key];
  }
  return DEFAULT_PIE_TITLE_INDEX[key];
}

/** Legacy pie_titles[1..3] when stored in branchen → regionen → marktkap field order */
const LEGACY_FIELD_ORDER_PIE_TITLE_AT_INDEX: Record<number, string> = {
  1: 'BRANCHEN',
  2: 'REGIONEN',
  3: 'MARKTKAPITALISIERUNG',
};

function isLegacyFieldOrderPieTitlesArray(pieTitles: string[]): boolean {
  const t1 = pieTitles[1]?.trim();
  if (!t1) return false;
  if (t1.includes('VERMÖGENSVERTEILUNG')) return false;
  return (
    t1 === LEGACY_FIELD_ORDER_PIE_TITLE_AT_INDEX[1] ||
    t1 === LEGACY_FIELD_ORDER_PIE_TITLE_AT_INDEX[2] ||
    pieTitles[2]?.trim() === LEGACY_FIELD_ORDER_PIE_TITLE_AT_INDEX[2]
  );
}

/**
 * Remap pie_titles from legacy field order to display-order indices (Long-Term Value Fund).
 */
export function normalizePieTitlesForTemplate(
  pieTitles: string[] | undefined,
  templateId?: string,
  displayOrderDefaults?: string[],
): string[] | undefined {
  if (!pieTitles?.length || !templateId || !displayOrderDefaults?.length) {
    return pieTitles;
  }
  if (templateId !== TEMPLATE_ID_LONG_TERM_VALUE_FUND) {
    return pieTitles;
  }
  if (!isLegacyFieldOrderPieTitlesArray(pieTitles)) {
    return pieTitles;
  }
  const top5 = pieTitles[0] ?? displayOrderDefaults[0];
  return [top5, ...displayOrderDefaults.slice(1)];
}

/** Title values stored at old pie_titles indices — ignore when template uses remapped indices */
export function isStalePieTitleFromLegacyFieldOrder(value: string | undefined): boolean {
  const v = value?.trim();
  if (!v) return false;
  return (
    v === 'BRANCHEN' ||
    v === 'REGIONEN' ||
    v === 'MARKTKAPITALISIERUNG' ||
    v === '1. BRANCHEN' ||
    v === '2. REGIONEN' ||
    v === '3. MARKTKAPITALISIERUNG'
  );
}

export function templateUsesRemappedPieTitleIndices(templateId?: string): boolean {
  return !!templateId && templateId in PIE_TITLE_INDEX_BY_TEMPLATE;
}
